import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button as MuiButton,
  Chip,
  MenuItem,
  Pagination,
  TextField,
  Typography
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchProducts, setSelectedCategory } from '../features/productsSlice';
import { fetchCategories } from '../features/categoriesSlice';
import { addToCart, addToGuestCart } from '../features/cartSlice';
import { addToWishlist, removeFromWishlist } from '../features/wishlistSlice';
import StatusMessage from '../components/common/StatusMessage';
import ProductGrid from '../components/products/ProductGrid';
import { Product } from '../types';
import { formatCurrency } from '../utils/pricing';
import { loginWithRedirect } from '../utils/navigation';
import useDocumentTitle from '../hooks/useDocumentTitle';
import { SORT_OPTIONS } from '../constants';

const ProductsPage: React.FC = () => {
  useDocumentTitle(
    'Shop',
    'Shop elegant earrings, necklaces, rings and bracelets at Vediluxe. Korean-inspired jewellery at budget-friendly prices with free shipping.'
  );
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, loading, error, currentPage, totalPages } = useAppSelector(
    (state) => state.products
  );
  const { items: categories } = useAppSelector((state) => state.categories);
  const { token } = useAppSelector((state) => state.auth);
  const wishlistedIds = useAppSelector((state) =>
    new Set((state.wishlist.wishlist?.items || []).map((item) => item.product?._id))
  );
  const [page, setPage] = useState(1);

  const categoryParam = searchParams.get('category');
  const searchQuery = searchParams.get('q');
  const maxPriceParam =
    searchParams.get('maxPrice') ||
    searchParams.get('maxprice') ||
    searchParams.get('price') ||
    searchParams.get('under');
  const colorParam = searchParams.get('color');
  const sortParam = searchParams.get('sort') || 'newest';
  const minRatingActive = searchParams.get('minRating') === '4';
  const maxPrice = maxPriceParam ? Number(maxPriceParam) : null;
  const hasPriceFilter = typeof maxPrice === 'number' && Number.isFinite(maxPrice) && maxPrice > 0;
  const hasColorFilter = Boolean(colorParam && colorParam.trim());
  const hasSearchQuery = Boolean(searchQuery && searchQuery.trim());
  const selectedCategory = useMemo(
    () =>
      categories.find(
        (c) => c.slug === categoryParam || c._id === categoryParam
      ) || null,
    [categories, categoryParam]
  );

  const selectedCategoryId = selectedCategory?._id || null;
  // A category param that matches nothing must still filter (to zero
  // results) — dropping it silently showed the entire catalog
  const unknownCategoryParam = Boolean(
    categoryParam && categories.length > 0 && !selectedCategory
  );
  const categoryFilterValue = selectedCategoryId || categoryParam || null;
  const hasActiveFilter =
    Boolean(categoryFilterValue) || hasPriceFilter || hasColorFilter || hasSearchQuery;

  const storefrontCollections = useMemo(() => {
    const byParentId: Record<string, typeof categories> = {};
    categories.forEach((category) => {
      const parentId = category.parent?._id;
      if (!parentId) return;
      if (!byParentId[parentId]) byParentId[parentId] = [];
      byParentId[parentId].push(category);
    });

    const roots = categories.filter((c) => !c.parent?._id);
    const collectionMap = new Map<
      string,
      { id: string; slug: string; name: string; description: string; group: string }
    >();

    roots.forEach((root) => {
      const rootChildren = byParentId[root._id] || [];

      // If a root has a single structural child (e.g. Women -> Jewellery),
      // show one level deeper for a cleaner, Shopify-like collection grid.
      if (rootChildren.length === 1) {
        const structuralChild = rootChildren[0];
        const grandChildren = byParentId[structuralChild._id] || [];
        if (grandChildren.length > 0) {
          grandChildren.forEach((child) => {
            collectionMap.set(child._id, {
              id: child._id,
              slug: child.slug,
              name: child.name,
              description: child.description,
              group: root.name
            });
          });
          return;
        }
      }

      if (rootChildren.length > 0) {
        rootChildren.forEach((child) => {
          collectionMap.set(child._id, {
            id: child._id,
            slug: child.slug,
            name: child.name,
            description: child.description,
            group: root.name
          });
        });
      } else {
        collectionMap.set(root._id, {
          id: root._id,
          slug: root.slug,
          name: root.name,
          description: root.description,
          group: 'Collections'
        });
      }
    });

    const computed = Array.from(collectionMap.values());
    if (computed.length > 0) return computed;

    // Fallback only if hierarchy is missing/unexpected.
    return categories
      .filter((c) => !categories.some((other) => other.parent?._id === c._id))
      .map((c) => ({
        id: c._id,
        slug: c.slug,
        name: c.name,
        description: c.description,
        group: c.parent?.name || 'Collections'
      }));
  }, [categories]);

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    dispatch(setSelectedCategory(selectedCategoryId));
    setPage(1);
  }, [dispatch, selectedCategoryId, maxPriceParam, colorParam, searchQuery]);

  useEffect(() => {
    dispatch(
      fetchProducts({
        // Pass the raw param through when it doesn't resolve locally —
        // the API accepts slugs and returns empty for unknown ones
        categoryId: categoryFilterValue || undefined,
        maxPrice: hasPriceFilter ? maxPrice : undefined,
        color: hasColorFilter ? colorParam || undefined : undefined,
        q: hasSearchQuery ? searchQuery || undefined : undefined,
        sortBy: sortParam,
        minRating: minRatingActive ? 4 : undefined,
        page,
        limit: 24
      })
    );
  }, [dispatch, hasPriceFilter, hasColorFilter, hasSearchQuery, maxPrice, colorParam, searchQuery, categoryFilterValue, sortParam, minRatingActive, page]);

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value) {
      next.set(key, value);
    } else {
      next.delete(key);
    }
    setSearchParams(next, { replace: true });
    setPage(1);
  };

  if (error) {
    return <StatusMessage variant="error" message={error} />;
  }

  if (loading && items.length === 0) {
    return <StatusMessage variant="loading" />;
  }

  const handleAddToCart = async (product: Product) => {
    if (!token) {
      dispatch(addToGuestCart({ product, quantity: 1 }));
      return;
    }
    await dispatch(addToCart({ productId: product._id, quantity: 1 }));
  };

  const handleToggleWishlist = async (product: Product) => {
    if (!token) {
      navigate(loginWithRedirect(window.location.pathname + window.location.search));
      return;
    }
    if (wishlistedIds.has(product._id)) {
      await dispatch(removeFromWishlist({ productId: product._id }));
      return;
    }
    await dispatch(addToWishlist({ productId: product._id }));
  };

  const pageTitle = hasSearchQuery
    ? `Results for "${searchQuery}"`
    : selectedCategory?.name
      || (hasPriceFilter ? `Under ${formatCurrency(maxPrice)}` : 'All products');

  return (
    <>
      {/* Category filter chips */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 2 }}>
          {pageTitle}
          {hasColorFilter && ` — ${colorParam}`}
        </Typography>
        <Box sx={{
          display: 'flex',
          gap: 1,
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          overflowX: { xs: 'auto', md: 'visible' },
          pb: { xs: 1, md: 0 },
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}>
          <Chip
            label="All"
            variant={!hasActiveFilter ? 'filled' : 'outlined'}
            color={!hasActiveFilter ? 'primary' : 'default'}
            onClick={() => setSearchParams({})}
          />
          {storefrontCollections.slice(0, 10).map((cat) => (
            <Chip
              key={cat.id}
              label={cat.name}
              variant={selectedCategoryId === cat.id ? 'filled' : 'outlined'}
              color={selectedCategoryId === cat.id ? 'primary' : 'default'}
              onClick={() => setSearchParams({ category: cat.slug })}
            />
          ))}
        </Box>

        {/* Sort + rating filter */}
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mt: 2, flexWrap: 'wrap' }}>
          <TextField
            select
            size="small"
            label="Sort by"
            value={sortParam}
            onChange={(e) => setParam('sort', e.target.value === 'newest' ? null : e.target.value)}
            sx={{ minWidth: 190 }}
          >
            {SORT_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
          <Chip
            icon={<StarIcon sx={{ fontSize: 16 }} />}
            label="4★ & up"
            variant={minRatingActive ? 'filled' : 'outlined'}
            color={minRatingActive ? 'primary' : 'default'}
            onClick={() => setParam('minRating', minRatingActive ? null : '4')}
          />
        </Box>
      </Box>

      {unknownCategoryParam && (
        <Alert
          severity="info"
          sx={{ mb: 2 }}
          action={
            <MuiButton color="inherit" size="small" onClick={() => setParam('category', null)}>
              Show all products
            </MuiButton>
          }
        >
          We couldn't find a collection called “{categoryParam}”.
        </Alert>
      )}

      <ProductGrid
        products={items}
        onAddToCart={handleAddToCart}
        onAddToWishlist={handleToggleWishlist}
      />

      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <Pagination
            page={currentPage}
            count={totalPages}
            color="primary"
            onChange={(_, nextPage) => setPage(nextPage)}
          />
        </Box>
      )}
    </>
  );
};

export default ProductsPage;
